import { AWARD_STAGE, type AwardStageValue } from './praiseTypes';
import { api, apiBlob, apiUpload } from '../../../lib/api';
import type { ListQuery, PagedResult } from '../../administration/users/userTypes';
import type { PersonSummary } from '../personnel/personnelTypes';
import type {
  AwardCategoryDetail,
  AwardCategoryListItem,
  AwardCategoryRequest,
  AwardDetail,
  AwardListItem,
  AwardListQuery,
  AwardNomineeDetail,
  AwardNomineeListItem,
  AwardNomineeListQuery,
  AwardNomineeRequest,
  AwardCreateRequest,
  AwardCriterionInput,
  AwardRequest,
  AwardDeliberationRequest,
  AwardFinalAwardingRequest,
  QualifyNomineeRequest,
  SaveAwardScoreRequest,
} from './praiseTypes';

function toQueryString(query: ListQuery & AwardListQuery & AwardNomineeListQuery): string {
  const params = new URLSearchParams();

  if (query.search?.trim()) params.set('search', query.search.trim());
  if (query.page && query.page > 1) params.set('page', String(query.page));
  if (query.pageSize) params.set('pageSize', String(query.pageSize));
  if (query.sort) params.set('sort', query.sort);

  // Present-but-empty is "ticked to nothing", which matches no row — so `!== undefined`.
  if (query.status !== undefined) params.set('status', query.status);
  if (query.category !== undefined) params.set('category', query.category);
  if (query.includeDeleted) params.set('includeDeleted', 'true');
  if (query.stage) params.set('stage', query.stage);

  const serialized = params.toString();
  return serialized ? `?${serialized}` : '';
}

const CATEGORIES = '/api/award-categories';

export const awardCategoriesApi = {
  list: (query: ListQuery, signal?: AbortSignal) =>
    api<PagedResult<AwardCategoryListItem>>(`${CATEGORIES}${toQueryString(query)}`, { signal }),

  /** The live categories, each with its live checklist — the award form's picker. */
  options: (signal?: AbortSignal) => api<AwardCategoryDetail[]>(`${CATEGORIES}/options`, { signal }),

  get: (id: string, signal?: AbortSignal) => api<AwardCategoryDetail>(`${CATEGORIES}/${id}`, { signal }),

  create: (body: AwardCategoryRequest) => api<AwardCategoryDetail>(CATEGORIES, { method: 'POST', body }),

  /** The Details section's own write — name and description, nothing of the checklist. */
  update: (id: string, body: { name: string; description: string | null }) =>
    api<AwardCategoryDetail>(`${CATEGORIES}/${id}`, { method: 'PUT', body }),

  /** The Checklist section's own write — the lines, nothing of the details. */
  updateChecklist: (id: string, body: { documents: AwardCategoryRequest['documents'] }) =>
    api<AwardCategoryDetail>(`${CATEGORIES}/${id}/documents`, { method: 'PUT', body }),

  deactivate: (id: string) => api<void>(`${CATEGORIES}/${id}`, { method: 'DELETE' }),

  activate: (id: string) => api<void>(`${CATEGORIES}/${id}/restore`, { method: 'POST' }),

  clear: () => api<number>(`${CATEGORIES}/clear-data`, { method: 'POST' }),
};

/**
 * The READS both surfaces make — Nomination's under `award-nominations.read`, Evaluation's under
 * `award-evaluations.read`. One shape, two bases, so the pages are handed the surface's own
 * endpoints and never copied.
 */
export interface AwardReadApi {
  list: (query: AwardListQuery, signal?: AbortSignal) => Promise<PagedResult<AwardListItem>>;
  get: (id: string, signal?: AbortSignal) => Promise<AwardDetail>;
  listNominees: (awardId: string, query: AwardNomineeListQuery, signal?: AbortSignal) => Promise<PagedResult<AwardNomineeListItem>>;
  getNominee: (nomineeId: string, signal?: AbortSignal) => Promise<AwardNomineeDetail>;
  /**
   * Fetches one submitted document. A FETCH then an object URL, never an `<a href>` at the
   * endpoint: the token travels in a header no navigation sends.
   */
  downloadDocument: (nomineeId: string, lineId: string, signal?: AbortSignal) => ReturnType<typeof apiBlob>;
  /** Display names for a page of nominees — PRAISE's own scoped door, never `/api/persons/resolve`. */
  names: (personIds: string[], signal?: AbortSignal) => Promise<PersonSummary[]>;
}

function readApi(base: string): AwardReadApi {
  return {
    list: (query, signal) => api<PagedResult<AwardListItem>>(`${base}${toQueryString(query)}`, { signal }),
    get: (id, signal) => api<AwardDetail>(`${base}/${id}`, { signal }),
    listNominees: (awardId, query, signal) =>
      api<PagedResult<AwardNomineeListItem>>(`${base}/${awardId}/nominees${toQueryString(query)}`, { signal }),
    getNominee: (nomineeId, signal) => api<AwardNomineeDetail>(`${base}/nominees/${nomineeId}`, { signal }),
    downloadDocument: (nomineeId, lineId, signal) =>
      apiBlob(`${base}/nominees/${nomineeId}/documents/${lineId}/file`, { signal }),
    names: (personIds, signal) => api<PersonSummary[]>(`${base}/names`, { method: 'POST', body: { personIds }, signal }),
  };
}

const NOMINATIONS = '/api/award-nominations';

/** PRAISE › Nomination — reads, and every write. */
export const nominationsApi = {
  ...readApi(NOMINATIONS),

  /** The award and its criteria in one save. */
  create: (body: AwardCreateRequest) => api<AwardDetail>(NOMINATIONS, { method: 'POST', body }),

  /** The Criteria section's own write — refused once scoring has started. */
  updateCriteria: (id: string, criteria: AwardCriterionInput[]) =>
    api<AwardDetail>(`${NOMINATIONS}/${id}/criteria`, { method: 'PUT', body: { criteria } }),

  update: (id: string, body: AwardRequest) => api<AwardDetail>(`${NOMINATIONS}/${id}`, { method: 'PUT', body }),

  remove: (id: string) => api<void>(`${NOMINATIONS}/${id}`, { method: 'DELETE' }),

  restore: (id: string) => api<void>(`${NOMINATIONS}/${id}/restore`, { method: 'POST' }),

  createNominee: (awardId: string, body: AwardNomineeRequest) =>
    api<AwardNomineeDetail>(`${NOMINATIONS}/${awardId}/nominees`, { method: 'POST', body }),

  updateNominee: (nomineeId: string, body: AwardNomineeRequest) =>
    api<AwardNomineeDetail>(`${NOMINATIONS}/nominees/${nomineeId}`, { method: 'PUT', body }),

  removeNominee: (nomineeId: string) =>
    api<void>(`${NOMINATIONS}/nominees/${nomineeId}`, { method: 'DELETE' }),

  restoreNominee: (nomineeId: string) =>
    api<void>(`${NOMINATIONS}/nominees/${nomineeId}/restore`, { method: 'POST' }),

  /** Files the document for one checklist line, or replaces it. Through `apiUpload` — multipart, real progress. */
  uploadDocument: (nomineeId: string, lineId: string, file: File, onProgress?: (percent: number) => void) => {
    const form = new FormData();
    form.append('file', file);

    return apiUpload<AwardNomineeDetail>(`${NOMINATIONS}/nominees/${nomineeId}/documents/${lineId}`, form, {
      onProgress,
    });
  },
};

const EVALUATIONS = '/api/award-evaluations';

/**
 * PRAISE › Evaluation — reads, the caller's OWN score sheet (`award-evaluations.score`), and the STAGE
 * GATES, each under its own key: qualify · deliberate · award.
 */
export const evaluationsApi = {
  ...readApi(EVALUATIONS),

  saveScore: (nomineeId: string, body: SaveAwardScoreRequest) =>
    api<AwardNomineeDetail>(`${EVALUATIONS}/nominees/${nomineeId}/my-score`, { method: 'PUT', body }),

  /** Requirements Qualification — Qualified or Disqualified, with remarks (required when disqualified). */
  qualify: (nomineeId: string, body: QualifyNomineeRequest) =>
    api<AwardNomineeDetail>(`${EVALUATIONS}/nominees/${nomineeId}/qualification`, { method: 'PUT', body }),

  /** Deliberation — the winners and remarks. Re-savable until the final awarding. */
  deliberate: (awardId: string, body: AwardDeliberationRequest) =>
    api<AwardDetail>(`${EVALUATIONS}/${awardId}/deliberation`, { method: 'PUT', body }),

  /** Final Awarding — ONE-WAY; writes each approved winner's Recognition on their 201 file. */
  finalize: (awardId: string, body: AwardFinalAwardingRequest) =>
    api<AwardDetail>(`${EVALUATIONS}/${awardId}/final-awarding`, { method: 'PUT', body }),

  /** Attaches a file to a stage. `stage` picks the route, and so the key the server checks. */
  uploadStageDocument: (awardId: string, stage: AwardStageValue, file: File, onProgress?: (percent: number) => void) => {
    const form = new FormData();
    form.append('file', file);

    return apiUpload<AwardDetail>(`${EVALUATIONS}/${awardId}/${STAGE_ROUTE[stage]}/documents`, form, { onProgress });
  },

  removeStageDocument: (awardId: string, stage: AwardStageValue, documentId: string) =>
    api<AwardDetail>(`${EVALUATIONS}/${awardId}/${STAGE_ROUTE[stage]}/documents/${documentId}`, { method: 'DELETE' }),

  downloadStageDocument: (awardId: string, documentId: string, signal?: AbortSignal) =>
    apiBlob(`${EVALUATIONS}/${awardId}/stage-documents/${documentId}/file`, { signal }),
};

/** Each stage's own route segment — the server gates the route, so the stage and its key cannot be mismatched. */
const STAGE_ROUTE: Record<AwardStageValue, string> = {
  [AWARD_STAGE.Deliberation]: 'deliberation',
  [AWARD_STAGE.FinalAwarding]: 'final-awarding',
};
